/**
 * Push Notification Service
 * Handles Web Push subscription and registration
 */

import { API_BASE_URL } from '../config/api';
import { amplifyAuthService } from '../auth/amplifyAuthService';

/**
 * Convert VAPID public key from base64 URL-safe format to Uint8Array
 * This is required by the Push API
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * Check if browser supports Service Worker and Push Manager
 */
export function isPushSupported(): boolean {
    return (
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window
    );
}

/**
 * Request notification permission from user
 * Returns 'granted', 'denied', or 'default'
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
        throw new Error('This browser does not support notifications');
    }
    return await Notification.requestPermission();
}

/**
 * Get VAPID public key from backend
 */
async function getVapidPublicKey(): Promise<string> {
    try {
        const response = await fetch(`${API_BASE_URL}/vapid-public-key`);
        if (!response.ok) {
            throw new Error(`Failed to get VAPID key: ${response.statusText}`);
        }
        const data = await response.json();
        return data.publicKey;
    } catch (error) {
        console.error('Error getting VAPID public key:', error);
        throw error;
    }
}

/**
 * Register service worker and subscribe to push notifications
 * @param userId - User ID to associate with the subscription
 * @returns Promise<boolean> - True if subscription was successful
 */
export async function registerAndSubscribe(userId: string): Promise<boolean> {
    try {
        // Check browser support
        if (!isPushSupported()) {
            console.log('Browser does not support Service Worker / Push');
            return false;
        }

        // Register service worker
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('Service worker registered:', registration);

        // Wait for service worker to be ready and control the page
        await navigator.serviceWorker.ready;
        console.log('Service worker is ready');

        // Check if service worker is controlling the page
        if (navigator.serviceWorker.controller) {
            console.log('✅ Service worker is controlling the page:', navigator.serviceWorker.controller.scriptURL);
        } else {
            console.warn('⚠️ Service worker is not controlling the page yet.');
            console.warn('⚠️ Push notifications will not work until service worker controls the page.');
            console.warn('⚠️ Please refresh the page (Ctrl+Shift+R or Cmd+Shift+R) to activate service worker.');
            // Service worker will control after reload, but we can continue with subscription
            // Note: Push events will only work after page reload when service worker controls the page
        }

        // Handle service worker updates
        registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
                newWorker.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                        // New service worker available, prompt user to refresh
                        console.log('New service worker available, refreshing...');
                        // Auto-refresh to activate new service worker
                        window.location.reload();
                    }
                });
            }
        });

        // Wait for service worker to be ready
        if (registration.waiting) {
            console.log('Service worker is waiting, activating...');
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        // Request notification permission
        const permission = await requestNotificationPermission();
        if (permission !== 'granted') {
            console.log('User did not grant notification permission');
            return false;
        }

        // Get VAPID public key from backend
        const publicKey = await getVapidPublicKey();
        if (!publicKey) {
            throw new Error('VAPID public key not available');
        }

        // Subscribe to push notifications
        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
        });

        console.log('Push subscription created:', subscription);
        console.log('Subscription endpoint:', subscription.endpoint);

        // Send subscription to backend
        const subscriptionData = subscription.toJSON();
        console.log('Subscription data to send:', {
            endpoint: subscriptionData.endpoint?.substring(0, 50) + '...',
            hasKeys: !!subscriptionData.keys,
        });

        // Get authentication token
        const authHeaders = await amplifyAuthService.getAuthHeader();

        const response = await fetch(`${API_BASE_URL}/save-subscription`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...authHeaders,
            },
            body: JSON.stringify({
                subscription: subscriptionData,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
                `Failed to save subscription: ${errorData.detail || response.statusText}`
            );
        }

        const result = await response.json();
        console.log('Subscription saved to backend successfully:', result);
        console.log('✅ Subscription endpoint saved:', subscriptionData.endpoint?.substring(0, 50) + '...');
        return true;
    } catch (error) {
        console.error('Error registering push subscription:', error);
        return false;
    }
}

/**
 * Unsubscribe from push notifications and remove subscription from backend
 * @param userId - User ID
 */
export async function unsubscribe(userId: string): Promise<boolean> {
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
            // Unsubscribe from push service
            await subscription.unsubscribe();
            console.log('Unsubscribed from push notifications');
        }

        // Remove subscription from backend
        const authHeaders = await amplifyAuthService.getAuthHeader();

        const response = await fetch(`${API_BASE_URL}/subscription`, {
            method: 'DELETE',
            headers: {
                ...authHeaders,
            },
        });

        if (!response.ok) {
            console.warn('Failed to delete subscription from backend');
        }

        return true;
    } catch (error) {
        console.error('Error unsubscribing:', error);
        return false;
    }
}

/**
 * Check if user is currently subscribed to push notifications
 */
export async function isSubscribed(): Promise<boolean> {
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        return subscription !== null;
    } catch (error) {
        console.error('Error checking subscription status:', error);
        return false;
    }
}

